import { useState } from 'react'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import type { AuditLog, AuditLogListResponse } from '@academy/types'
import { api } from '../lib/api.js'
import { PageHeader } from './PageHeader.js'
import { DataTable, type Column } from './DataTable.js'
import { StatusPill } from './StatusPill.js'
import { Icon } from './Icon.js'

const PAGE_SIZE = 50

interface AuditTrailProps {
  /** API path, e.g. '/api/v1/audit-logs' or '/api/v1/super/audit-logs'. */
  endpoint: string
  /** Query-key prefix so the academy + super views cache separately. */
  queryKey: readonly string[]
  title: string
  description: string
  /** Super view shows the academy column; academy view hides it. */
  showAcademy?: boolean
}

interface Filters {
  action: string
  actorEmail: string
}

/** Tone for the actor-role pill. */
function roleTone(role: string): string {
  if (role === 'system') return 'neutral'
  if (role === 'super_admin') return 'suspended'
  if (role === 'academy_admin') return 'active'
  return 'trial'
}

export function AuditTrail({
  endpoint,
  queryKey,
  title,
  description,
  showAcademy = false,
}: AuditTrailProps) {
  const [page, setPage] = useState(1)
  const [filters, setFilters] = useState<Filters>({ action: '', actorEmail: '' })
  const [expanded, setExpanded] = useState<string | null>(null)

  const params = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE) })
  if (filters.action.trim()) params.set('action', filters.action.trim())

  const logs = useQuery({
    queryKey: [...queryKey, page, filters.action] as const,
    queryFn: async (): Promise<AuditLogListResponse> => {
      // The endpoint returns { data, meta } at the top level. The api<T> helper
      // models the success body as { data: T }, so cast through to recover meta.
      const res = (await api<AuditLog[]>(`${endpoint}?${params.toString()}`)) as
        | AuditLogListResponse
        | { error: { code: string; message: string } }
      if ('error' in res) throw new Error(res.error.message)
      return res
    },
    placeholderData: keepPreviousData,
  })

  const rows = logs.data?.data ?? []
  const total = logs.data?.meta.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  // The actor-email filter is applied client-side over the current page (the
  // API filters by actorUserId, not email; email is the human-friendly field).
  const visible = filters.actorEmail.trim()
    ? rows.filter((r) =>
        (r.actorEmail ?? '').toLowerCase().includes(filters.actorEmail.trim().toLowerCase()),
      )
    : rows

  const columns: Column<AuditLog>[] = [
    {
      key: 'createdAt',
      header: 'When',
      render: (r) => (
        <span className="whitespace-nowrap text-fg-muted tabular-nums">
          {new Date(r.createdAt).toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </span>
      ),
    },
    {
      key: 'actor',
      header: 'Actor',
      render: (r) => (
        <div className="flex flex-col">
          <span className="font-medium text-fg">{r.actorEmail ?? 'System'}</span>
          <StatusPill tone={roleTone(r.actorRole)}>{r.actorRole.replace('_', ' ')}</StatusPill>
        </div>
      ),
    },
    {
      key: 'action',
      header: 'Action',
      render: (r) => (
        <code className="rounded bg-surface-muted px-1.5 py-0.5 text-xs text-fg">{r.action}</code>
      ),
    },
    {
      key: 'resource',
      header: 'Resource',
      render: (r) =>
        r.resourceType ? (
          <span className="text-fg-muted">
            {r.resourceType}
            {r.resourceId ? (
              <span className="text-fg-faint"> · {r.resourceId.slice(0, 8)}</span>
            ) : null}
          </span>
        ) : (
          <span className="text-fg-faint">—</span>
        ),
    },
    {
      key: 'summary',
      header: 'Summary',
      render: (r) => <span className="text-fg">{r.summary ?? ''}</span>,
    },
    ...(showAcademy
      ? [
          {
            key: 'academy',
            header: 'Academy',
            render: (r: AuditLog) =>
              r.academyId ? (
                <span className="text-fg-faint">{r.academyId.slice(0, 8)}</span>
              ) : (
                <StatusPill tone="neutral">platform</StatusPill>
              ),
          },
        ]
      : []),
    {
      key: 'detail',
      header: '',
      align: 'right' as const,
      render: (r: AuditLog) => {
        const hasDiff = r.oldValues != null || r.newValues != null
        if (!hasDiff) return null
        const open = expanded === r.id
        return (
          <button
            type="button"
            onClick={() => setExpanded(open ? null : r.id)}
            className="inline-flex items-center gap-1 text-xs text-fg-muted hover:text-[var(--color-primary)]"
          >
            {open ? 'Hide' : 'Diff'}
            <Icon name={open ? 'chevronDown' : 'chevronRight'} size={12} />
          </button>
        )
      },
    },
  ]

  return (
    <div className="space-y-6">
      <PageHeader title={title} description={description} />

      <div className="flex flex-wrap items-center gap-2">
        <label className="relative min-w-[180px]">
          <Icon
            name="search"
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-faint"
          />
          <input
            value={filters.action}
            onChange={(e) => {
              setPage(1)
              setFilters((f) => ({ ...f, action: e.target.value }))
            }}
            placeholder="Filter by action…"
            className="w-full rounded-md border border-border bg-surface pl-9 pr-3 py-2 text-sm text-fg placeholder-fg-faint shadow-sm focus:outline-none focus:border-[var(--color-primary)] focus:ring-1 focus:ring-[var(--color-primary)]"
          />
        </label>
        <label className="relative min-w-[180px]">
          <Icon
            name="search"
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-faint"
          />
          <input
            value={filters.actorEmail}
            onChange={(e) => setFilters((f) => ({ ...f, actorEmail: e.target.value }))}
            placeholder="Filter by actor email…"
            className="w-full rounded-md border border-border bg-surface pl-9 pr-3 py-2 text-sm text-fg placeholder-fg-faint shadow-sm focus:outline-none focus:border-[var(--color-primary)] focus:ring-1 focus:ring-[var(--color-primary)]"
          />
        </label>
        {logs.isError ? (
          <span className="text-sm text-danger">Failed to load: {logs.error.message}</span>
        ) : null}
      </div>

      <DataTableWithDiff
        columns={columns}
        rows={visible}
        loading={logs.isLoading}
        expanded={expanded}
        colSpan={columns.length}
      />

      <div className="flex items-center justify-between text-sm text-fg-muted">
        <span>
          {total > 0
            ? `Page ${page} of ${totalPages} · ${total} event${total === 1 ? '' : 's'}`
            : 'No events'}
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="rounded-md border border-border px-3 py-1.5 disabled:opacity-40 hover:bg-surface-muted"
          >
            Previous
          </button>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="rounded-md border border-border px-3 py-1.5 disabled:opacity-40 hover:bg-surface-muted"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * Wraps DataTable to render an expandable old→new diff panel beneath the
 * expanded row. DataTable itself is row-agnostic, so the diff lives in a
 * sibling region keyed to the expanded id.
 */
function DataTableWithDiff({
  columns,
  rows,
  loading,
  expanded,
  colSpan: _colSpan,
}: {
  columns: Column<AuditLog>[]
  rows: AuditLog[]
  loading: boolean
  expanded: string | null
  colSpan: number
}) {
  const expandedRow = rows.find((r) => r.id === expanded)
  return (
    <div className="space-y-3">
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        loading={loading}
        density="compact"
        stickyHeader
        empty="No activity recorded yet."
      />
      {expandedRow ? <DiffPanel log={expandedRow} /> : null}
    </div>
  )
}

function DiffPanel({ log }: { log: AuditLog }) {
  return (
    <div className="rounded-lg border border-border bg-surface-muted p-4">
      <div className="mb-2 text-xs font-medium uppercase tracking-wide text-fg-subtle">
        Change detail · {log.action}
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <DiffColumn label="Before" value={log.oldValues} />
        <DiffColumn label="After" value={log.newValues} />
      </div>
      {log.metadata != null ? (
        <div className="mt-4">
          <DiffColumn label="Context" value={log.metadata} />
        </div>
      ) : null}
    </div>
  )
}

function DiffColumn({ label, value }: { label: string; value: unknown }) {
  return (
    <div>
      <div className="mb-1 text-xs font-medium text-fg-subtle">{label}</div>
      {value == null ? (
        <span className="text-sm text-fg-faint">—</span>
      ) : (
        <pre className="max-h-64 overflow-auto rounded-md border border-border bg-surface p-3 text-xs text-fg">
          {JSON.stringify(value, null, 2)}
        </pre>
      )}
    </div>
  )
}
